import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KanbanSquare, Plus, Trash2, ArrowUp, ArrowDown, CheckSquare } from 'lucide-react';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { tareasKeys, useEtiquetas, usePlantillaColumnas } from '@/hooks/useTareas';
import type { PlantillaColumna } from '@/types/tareas';
import { AutomatizacionesPanel } from './AutomatizacionesPanel';

const campo = 'px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';
const COLOR_DEFAULT = '#94a3b8';
const COLOR_ETIQUETA_DEFAULT = '#0e85bf';

export function TareasConfigPanel() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <KanbanSquare size={18} className="text-cyan-600" />
          <h3 className="font-semibold text-text-primary">Tareas</h3>
        </div>
        <p className="text-sm text-text-secondary">
          Define las columnas con las que nace cada tablero nuevo y las etiquetas disponibles para clasificar tareas.
        </p>
      </div>
      <PlantillaColumnas />
      <Etiquetas />
      <AutomatizacionesPanel />
    </div>
  );
}

function PlantillaColumnas() {
  const qc = useQueryClient();
  const { data: guardada } = usePlantillaColumnas();
  const [columnas, setColumnas] = useState<PlantillaColumna[]>([]);

  useEffect(() => { if (guardada) setColumnas(guardada); }, [guardada]);

  const guardar = useMutation({
    mutationFn: (cols: PlantillaColumna[]) => api.patch('/api/tareas/plantilla-columnas', { columnas: cols }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: tareasKeys.plantilla }); toast.success('Plantilla guardada'); },
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo guardar la plantilla')),
  });

  const cambiar = (i: number, cambios: Partial<PlantillaColumna>) =>
    setColumnas(prev => prev.map((c, idx) => (idx === i ? { ...c, ...cambios } : c)));
  const mover = (i: number, delta: -1 | 1) =>
    setColumnas(prev => {
      const d = i + delta;
      if (d < 0 || d >= prev.length) return prev;
      const copia = [...prev];
      [copia[i], copia[d]] = [copia[d], copia[i]];
      return copia;
    });

  const hayCambios = JSON.stringify(columnas) !== JSON.stringify(guardada ?? []);
  const valida = columnas.length > 0 && columnas.every(c => c.nombre.trim());

  return (
    <div>
      <h4 className="text-sm font-semibold text-text-primary mb-2">Plantilla de columnas</h4>
      <ul className="space-y-2">
        {columnas.map((c, i) => (
          <li key={i} className="flex items-center gap-2">
            <input type="color" value={c.color ?? COLOR_DEFAULT} onChange={e => cambiar(i, { color: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5" aria-label="Color" />
            <input value={c.nombre} onChange={e => cambiar(i, { nombre: e.target.value })} maxLength={100}
              className={cn(campo, 'flex-1 min-w-0')} aria-label="Nombre de la columna" />
            <label className="inline-flex items-center gap-1 text-xs text-text-secondary whitespace-nowrap cursor-pointer">
              <input type="checkbox" checked={!!c.esFinal} onChange={e => cambiar(i, { esFinal: e.target.checked })} />
              <CheckSquare size={12} /> Final
            </label>
            <button onClick={() => mover(i, -1)} disabled={i === 0} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Subir"><ArrowUp size={14} /></button>
            <button onClick={() => mover(i, 1)} disabled={i === columnas.length - 1} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Bajar"><ArrowDown size={14} /></button>
            <button onClick={() => setColumnas(prev => prev.filter((_, idx) => idx !== i))} disabled={columnas.length === 1}
              className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30" aria-label="Quitar columna"><Trash2 size={14} /></button>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => setColumnas(prev => [...prev, { nombre: '' }])} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
          <Plus size={14} /> Agregar columna
        </button>
        <button onClick={() => guardar.mutate(columnas.map(c => ({ ...c, nombre: c.nombre.trim() })))}
          disabled={!hayCambios || !valida || guardar.isPending}
          className="px-4 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50">
          Guardar plantilla
        </button>
      </div>
      <p className="text-[11px] text-text-secondary mt-2">Solo afecta a los tableros que crees a partir de ahora.</p>
    </div>
  );
}

function Etiquetas() {
  const qc = useQueryClient();
  const { data: etiquetas = [] } = useEtiquetas();
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState(COLOR_ETIQUETA_DEFAULT);

  const refrescar = () => qc.invalidateQueries({ queryKey: tareasKeys.etiquetas });
  const alFallar = (e: unknown) => toast.error(getErrorMessage(e, 'No se pudo guardar la etiqueta'));

  const crear = useMutation({
    mutationFn: () => api.post('/api/tareas/etiquetas', { nombre: nombre.trim(), color }).then(r => r.data),
    onSuccess: () => { setNombre(''); refrescar(); },
    onError: alFallar,
  });
  const actualizar = useMutation({
    mutationFn: ({ id, ...body }: { id: string; nombre?: string; color?: string }) =>
      api.patch(`/api/tareas/etiquetas/${id}`, body).then(r => r.data),
    onSuccess: refrescar,
    onError: alFallar,
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/api/tareas/etiquetas/${id}`).then(r => r.data),
    onSuccess: () => { refrescar(); qc.invalidateQueries({ queryKey: ['tareas'] }); },
    onError: alFallar,
  });

  return (
    <div>
      <h4 className="text-sm font-semibold text-text-primary mb-2">Etiquetas</h4>
      <ul className="space-y-2">
        {etiquetas.map(e => (
          <li key={e.id} className="flex items-center gap-2">
            <input type="color" defaultValue={e.color} onBlur={ev => { if (ev.target.value !== e.color) actualizar.mutate({ id: e.id, color: ev.target.value }); }}
              className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5" aria-label="Color" />
            <input defaultValue={e.nombre} maxLength={60}
              onBlur={ev => {
                const valor = ev.target.value.trim();
                if (valor && valor !== e.nombre) actualizar.mutate({ id: e.id, nombre: valor });
                else ev.target.value = e.nombre;
              }}
              className={cn(campo, 'flex-1 min-w-0')} aria-label="Nombre de la etiqueta" />
            <button onClick={() => eliminar.mutate(e.id)} className="p-1.5 text-gray-400 hover:text-red-500" aria-label="Eliminar etiqueta"><Trash2 size={14} /></button>
          </li>
        ))}
        {!etiquetas.length && <li className="text-sm text-text-secondary">Aún no hay etiquetas.</li>}
      </ul>
      <form onSubmit={e => { e.preventDefault(); if (nombre.trim()) crear.mutate(); }} className="flex items-center gap-2 mt-3">
        <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5" aria-label="Color de la nueva etiqueta" />
        <input value={nombre} onChange={e => setNombre(e.target.value)} maxLength={60} placeholder="Nueva etiqueta" className={cn(campo, 'flex-1 min-w-0')} />
        <button type="submit" disabled={!nombre.trim() || crear.isPending} className="px-4 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50">
          Agregar
        </button>
      </form>
    </div>
  );
}
